/**
 * <pg-facets>: filters, sort and pagination for collection and search pages
 * (markup: snippets/pg-facets.liquid).
 *
 * - Every change re-renders the section through the Section Rendering API, aborting stale
 *   requests, and maps the response back onto stable ids: filter groups, active chips, the live
 *   count, the drawer footer and the results. The results swap inside a View Transition
 *   (`withTransition`) so the grid crossfades. The URL follows with history.replaceState;
 *   popstate re-renders from the URL, so back/forward land on the right state.
 * - Filter groups keep their open / "show all" / find-as-you-type state across re-renders, and a
 *   price field the shopper is using is never replaced under their fingers.
 * - Price: two number inputs plus a dual-thumb slider, debounced.
 * - Below 1200px the same <form> moves into a drawer, which stays open while filtering; its
 *   footer button reads "Vezi N produse" from the fresh render.
 * - Infinite scroll: an IntersectionObserver near the end of the grid follows the real
 *   `rel="next"` link (crawlers and no-JS shoppers use it as plain pagination). After a few
 *   automatic pages it waits for a click on "Încarcă mai multe produse" so the footer stays
 *   reachable. A skeleton row shows while a page loads.
 * - Every insertion dispatches `pg:content:rendered` (bubbles) so partner prices
 *   (@pg/wholesale) and other enhancers re-apply to the new cards.
 * - "In stock" on first visit (legacy rule, `data-default-stock`): applied once when the URL has
 *   no availability filter, unless the shopper removed it earlier in this session.
 */
import { PgElement, define, debounce, withTransition } from '@pg/core';
import { fetchSection } from '@pg/section-renderer';
import { openDialog } from '@pg/ui';

const desktop = matchMedia('(width >= 1200px)');
const STOCK_PARAM = 'filter.v.availability';
const STOCK_OPT_OUT = 'pg-facets-all-stock';
const AUTO_PAGES = 3;

/** @param {string} value */
const fold = (value) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Drop the Section Rendering API parameter from links that came back in a response. */
function cleanUrl(href) {
  const url = new URL(href, location.origin);
  url.searchParams.delete('section_id');
  return url.pathname + (url.search ? url.search : '');
}

function storage(action, value) {
  try {
    if (action === 'get') return sessionStorage.getItem(STOCK_OPT_OUT);
    if (action === 'set') sessionStorage.setItem(STOCK_OPT_OUT, value);
    if (action === 'remove') sessionStorage.removeItem(STOCK_OPT_OUT);
  } catch {
    /* storage blocked: the default simply applies again */
  }
  return null;
}

class PgFacets extends PgElement {
  #autoLoads = 0;
  #renderTicket = 0;
  #loadingMore = false;
  /** @type {IntersectionObserver|undefined} */
  #observer;

  mount() {
    this.sectionId = this.dataset.sectionId || '';
    const { signal } = this;

    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.addEventListener('input', (event) => this.#onInput(event), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.refs.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#commit();
    }, { signal });
    // The sort <select> sits outside the form (form="…"), so listen on the document for it.
    document.getElementById(`PgFacetsSort-${this.sectionId}`)?.addEventListener('change', () => this.#commit(), { signal });

    window.addEventListener('popstate', () => this.render(location.href, { history: false }), { signal });

    this.refs.drawer?.addEventListener('close', () => this.#dockForm(), { signal });
    desktop.addEventListener('change', () => {
      if (desktop.matches && this.refs.drawer?.open) this.refs.drawer.close();
    }, { signal });

    this.#debouncedCommit = debounce(() => this.#commit(), 650);
    this.#initRanges();
    this.#observe();
    this.#floatButton();
    this.#applyDefaultStock();
  }

  unmount() {
    this.#observer?.disconnect();
    this.#floatObserver?.disconnect();
  }

  /** @type {() => void} */
  #debouncedCommit = () => {};

  // ── Events ──────────────────────────────────────────────────────────────

  /** @param {Event} event */
  #onChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!this.refs.form?.contains(target)) return;
    if (target.type === 'checkbox') this.#commit();
    else if (target.matches('[data-pg-range-input]')) this.#debouncedCommit();
  }

  /** @param {Event} event */
  #onInput(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (target.matches('[data-pg-facet-find]')) return this.#find(target);
    const range = target.closest('[data-pg-range]');
    if (!range) return;
    if (target.matches('[data-pg-range-thumb]')) this.#thumbToInputs(range, target);
    else this.#inputsToThumbs(range);
    this.#debouncedCommit();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = /** @type {Element} */ (event.target);

    const open = target.closest('.pg-facets__open, .pg-facets__float');
    if (open) return this.openDrawer(/** @type {HTMLElement} */ (open));

    const more = target.closest('[data-pg-facet-more]');
    if (more) return this.#toggleMore(/** @type {HTMLButtonElement} */ (more));

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;

    const loadMore = target.closest('[data-pg-load-more]');
    if (loadMore) {
      event.preventDefault();
      this.#autoLoads = 0;
      this.loadMore({ focus: true });
      return;
    }

    const link = /** @type {HTMLAnchorElement|null} */ (target.closest('a[data-pg-facets-link]'));
    if (link) {
      event.preventDefault();
      this.render(link.href, { userAction: true });
    }
  }

  // ── Rendering ───────────────────────────────────────────────────────────

  /** Build the URL for the current form state (sort included through form="…"). */
  #formUrl() {
    const form = /** @type {HTMLFormElement|undefined} */ (this.refs.form);
    const params = new URLSearchParams();
    if (form) {
      for (const [key, value] of new FormData(form)) {
        if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
      }
    } else {
      const sort = /** @type {HTMLSelectElement|null} */ (document.getElementById(`PgFacetsSort-${this.sectionId}`));
      for (const [key, value] of new URLSearchParams(location.search)) if (key !== 'page' && key !== 'sort_by') params.append(key, value);
      if (sort) params.set('sort_by', sort.value);
    }
    this.#dropDefaultPrice(params);
    if (params.get('sort_by') === this.dataset.defaultSort) params.delete('sort_by');
    const query = params.toString();
    return `${this.dataset.baseUrl || location.pathname}${query ? `?${query}` : ''}`;
  }

  /** A price range that spans everything filters nothing; keep it out of the URL. */
  #dropDefaultPrice(params) {
    for (const range of this.querySelectorAll('[data-pg-range]')) {
      const max = Number(/** @type {HTMLElement} */ (range).dataset.max);
      const [min, top] = range.querySelectorAll('[data-pg-range-input]');
      const minName = /** @type {HTMLInputElement} */ (min).name;
      const maxName = /** @type {HTMLInputElement} */ (top).name;
      if (Number(params.get(minName)) <= 0) params.delete(minName);
      if (params.has(maxName) && Number(params.get(maxName)) >= max) params.delete(maxName);
    }
  }

  #commit() {
    this.render(this.#formUrl(), { userAction: true });
  }

  /**
   * Fetch the section for `url` and swap it in.
   * @param {string} url
   * @param {{ history?: boolean, userAction?: boolean, soft?: boolean }} [options]
   */
  async render(url, { history = true, userAction = false, soft = false } = {}) {
    const target = cleanUrl(url);
    const ticket = ++this.#renderTicket;
    const results = this.refs.results;
    results?.setAttribute('aria-busy', 'true');
    this.#announce('loading');
    try {
      const html = await fetchSection(this.sectionId, target, { key: `pg-facets-${this.sectionId}` });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (history && target !== location.pathname + location.search) {
        window.history.replaceState(window.history.state, '', target);
      }
      if (userAction) this.#rememberStock(target);
      await this.#swap(doc, { soft });
      this.#announce('');
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      console.warn('[PetGuru] Filters failed', error);
      this.#announce('error');
    } finally {
      if (ticket === this.#renderTicket) this.refs.results?.removeAttribute('aria-busy');
    }
  }

  /** @param {'loading'|'error'|''} state */
  #announce(state) {
    const status = this.refs.status;
    if (!status) return;
    status.textContent = state ? status.dataset[state] || '' : '';
  }

  /**
   * Map a fresh render onto the page.
   * @param {Document} doc
   * @param {{ soft?: boolean }} options
   */
  async #swap(doc, { soft = false } = {}) {
    const id = (name) => `${name}-${this.sectionId}`;
    const fresh = (name) => doc.getElementById(id(name));

    // Text-only updates keep live regions alive (the count is role="status").
    for (const name of ['PgFacetsCount']) {
      const el = document.getElementById(id(name));
      const next = fresh(name);
      if (el && next && el.textContent !== next.textContent) el.textContent = next.textContent;
    }
    for (const name of ['PgFacetsBadge', 'PgFacetsFloatBadge', 'PgFacetsShow', 'PgFacetsDrawerClear', 'PgFacetsActive']) {
      const el = document.getElementById(id(name));
      const next = fresh(name);
      if (!el || !next) continue;
      el.innerHTML = next.innerHTML;
      el.hidden = next.hidden;
    }
    this.#cleanLinks(document.getElementById(id('PgFacetsActive')));
    this.#cleanLinks(document.getElementById(id('PgFacetsDrawerClear'))?.parentElement);

    this.#swapGroups(doc);

    const results = this.refs.results;
    const nextResults = fresh('PgFacetsResults');
    if (!results || !nextResults) return;

    if (soft && this.#sameProducts(results, nextResults)) {
      // Same first page (e.g. the in-stock default changed nothing visible): only the paging changes.
      const nav = results.querySelector('[data-pg-pagination]');
      const nextNav = nextResults.querySelector('[data-pg-pagination]');
      if (nav && nextNav) nav.replaceWith(nextNav);
      this.#cleanLinks(results);
      return;
    }

    document.documentElement.classList.add('pg-facets-vt');
    try {
      await withTransition(() => {
        results.replaceChildren(...nextResults.childNodes);
        this.collectRefs();
        this.#cleanLinks(results);
      });
    } finally {
      document.documentElement.classList.remove('pg-facets-vt');
    }
    this.#autoLoads = 0;
    this.#observe();
    // Cards are back on the page: partner prices, reveals and anything else re-apply.
    results.dispatchEvent(new CustomEvent('pg:content:rendered', { bubbles: true }));

    // Keep the shopper's place: if the toolbar is above the viewport, bring the results top into view.
    const toolbar = this.querySelector('.pg-facets__toolbar');
    if (toolbar && toolbar.getBoundingClientRect().bottom < 0 && !this.refs.drawer?.open) {
      toolbar.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  /** @param {Element} a @param {Element} b */
  #sameProducts(a, b) {
    const ids = (el) => [...el.querySelectorAll('[data-product-id]')].map((item) => item.getAttribute('data-product-id')).join(',');
    return ids(a) === ids(b);
  }

  /** @param {Element|null|undefined} scope */
  #cleanLinks(scope) {
    for (const link of scope?.querySelectorAll('a[href*="section_id="]') || []) {
      link.setAttribute('href', cleanUrl(link.getAttribute('href') || ''));
    }
  }

  /** Replace each filter group, keeping open, expanded and find-as-you-type state. */
  #swapGroups(doc) {
    const form = this.refs.form;
    const nextForm = doc.getElementById(`PgFacetsForm-${this.sectionId}`);
    if (!form || !nextForm) return;
    const active = document.activeElement;

    for (const next of nextForm.querySelectorAll('details.pg-facet')) {
      const current = /** @type {HTMLDetailsElement|null} */ (form.querySelector(`#${CSS.escape(next.id)}`));
      if (!current) continue;
      // Never pull a price field out from under the shopper.
      if (current.contains(active) && current.dataset.pgFacet === 'price_range') continue;

      const nextDetails = /** @type {HTMLDetailsElement} */ (next);
      nextDetails.open = current.open;
      if (current.classList.contains('is-expanded')) {
        nextDetails.classList.add('is-expanded');
        const more = nextDetails.querySelector('[data-pg-facet-more]');
        if (more) {
          more.setAttribute('aria-expanded', 'true');
          more.textContent = /** @type {HTMLElement} */ (more).dataset.less || '';
        }
      }
      const query = /** @type {HTMLInputElement|null} */ (current.querySelector('[data-pg-facet-find]'))?.value;
      const focusedId = current.contains(active) ? active?.id : '';
      current.replaceWith(nextDetails);
      const find = /** @type {HTMLInputElement|null} */ (nextDetails.querySelector('[data-pg-facet-find]'));
      if (find && query) {
        find.value = query;
        this.#find(find);
      }
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
    }
    this.#initRanges();
  }

  // ── Infinite scroll ─────────────────────────────────────────────────────

  #observe() {
    this.#observer?.disconnect();
    const sentinel = this.refs.sentinel;
    if (!sentinel || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (this.#autoLoads >= AUTO_PAGES) return;
        this.#autoLoads += 1;
        this.loadMore();
      },
      // Well ahead of the end of the grid: the next page (and its placeholder cards) must be in
      // place before the footer scrolls into view, or the footer jumps away from the reader.
      { rootMargin: '0px 0px 1600px 0px' },
    );
    this.#observer.observe(sentinel);
  }

  /** Append the next page of cards. */
  async loadMore({ focus = false } = {}) {
    const link = /** @type {HTMLAnchorElement|null} */ (this.refs.results?.querySelector('[data-pg-load-more]'));
    const grid = this.refs.grid;
    if (!link || !grid || this.#loadingMore) return;
    this.#loadingMore = true;
    link.setAttribute('aria-busy', 'true');
    this.#announce('loading');

    const ghosts = this.#ghosts(grid);
    try {
      const html = await fetchSection(this.sectionId, cleanUrl(link.href), { key: `pg-facets-more-${this.sectionId}` });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextResults = doc.getElementById(`PgFacetsResults-${this.sectionId}`);
      const items = [...(nextResults?.querySelectorAll('.pg-facets__grid > li') || [])];
      ghosts.forEach((ghost) => ghost.remove());
      if (!items.length) throw new Error('no items');

      // Skip anything already on the page (the catalogue can shift between requests).
      const seen = new Set([...grid.querySelectorAll('[data-product-id]')].map((el) => el.getAttribute('data-product-id')));
      const fresh = items.filter((item) => !seen.has(item.getAttribute('data-product-id')));
      fresh.forEach((item) => item.classList.add('is-new'));
      grid.append(...fresh);

      const nav = this.refs.results?.querySelector('[data-pg-pagination]');
      const nextNav = nextResults?.querySelector('[data-pg-pagination]');
      if (nav && nextNav) nav.replaceWith(nextNav);
      this.collectRefs();
      this.#cleanLinks(this.refs.results);
      grid.dispatchEvent(new CustomEvent('pg:content:rendered', { bubbles: true }));

      if (focus && fresh[0]) /** @type {HTMLElement|null} */ (fresh[0].querySelector('.pg-card__title a'))?.focus({ preventScroll: false });
      this.#announce('');
    } catch (error) {
      ghosts.forEach((ghost) => ghost.remove());
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      console.warn('[PetGuru] Load more failed', error);
      this.#announce('error');
      this.#autoLoads = AUTO_PAGES; // stop auto-loading; the button (a real link) still works
    } finally {
      link.removeAttribute('aria-busy');
      this.#loadingMore = false;
    }
  }

  /** Show a row of skeleton cards while the next page loads. */
  #ghosts(grid) {
    const template = /** @type {HTMLTemplateElement|undefined} */ (this.refs.skeleton);
    if (!template) return [];
    const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length || 2;
    const ghosts = [];
    for (let i = 0; i < columns; i += 1) {
      const node = /** @type {Element} */ (template.content.firstElementChild?.cloneNode(true));
      if (node) ghosts.push(node);
    }
    grid.append(...ghosts);
    return ghosts;
  }

  // ── Drawer ──────────────────────────────────────────────────────────────

  /** @param {HTMLElement} opener */
  openDrawer(opener) {
    const { drawer, slot, form } = this.refs;
    if (!drawer || !slot || !form) return;
    slot.append(form);
    openDialog(drawer.id, opener.classList.contains('pg-facets__float') ? this.refs.openButton || opener : opener);
  }

  /** Put the form back in the sidebar when the drawer closes. */
  #dockForm() {
    const { sidebar, form } = this.refs;
    if (sidebar && form && form.parentElement !== sidebar) sidebar.append(form);
  }

  /** Below 1200px, a floating "Filtre" button appears once the toolbar scrolls away. */
  #floatObserver = /** @type {IntersectionObserver|undefined} */ (undefined);
  #floatButton() {
    const button = this.refs.floatButton;
    const toolbar = this.querySelector('.pg-facets__toolbar');
    if (!button || !toolbar || !('IntersectionObserver' in window)) return;
    let toolbarGone = false;
    let inside = false;
    const update = () => {
      const show = toolbarGone && inside;
      button.classList.toggle('is-visible', show);
      button.setAttribute('aria-hidden', String(!show));
      button.tabIndex = show ? 0 : -1;
    };
    this.#floatObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === toolbar) toolbarGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        else inside = entry.isIntersecting;
      }
      update();
    });
    this.#floatObserver.observe(toolbar);
    this.#floatObserver.observe(this.querySelector('.pg-facets__main') || this);
  }

  // ── Groups: show all, find, price range ─────────────────────────────────

  /** @param {HTMLButtonElement} button */
  #toggleMore(button) {
    const group = button.closest('.pg-facet');
    if (!group) return;
    const expanded = !group.classList.contains('is-expanded');
    group.classList.toggle('is-expanded', expanded);
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = (expanded ? button.dataset.less : button.dataset.more) || '';
  }

  /** @param {HTMLInputElement} input */
  #find(input) {
    const group = input.closest('.pg-facet');
    if (!group) return;
    const query = fold(input.value.trim());
    let matches = 0;
    for (const item of group.querySelectorAll('[data-pg-value]')) {
      const match = !query || fold(item.getAttribute('data-pg-value') || '').includes(query);
      /** @type {HTMLElement} */ (item).hidden = !match;
      if (match) matches += 1;
    }
    // While searching, every value is a candidate, not only the first eight.
    group.classList.toggle('is-searching', Boolean(query));
    if (query) group.classList.add('is-expanded');
    else if (group.querySelector('[data-pg-facet-more]')?.getAttribute('aria-expanded') !== 'true') group.classList.remove('is-expanded');
    const more = /** @type {HTMLElement|null} */ (group.querySelector('[data-pg-facet-more]'));
    if (more) more.hidden = Boolean(query);
    const empty = /** @type {HTMLElement|null} */ (group.querySelector('[data-pg-facet-nomatch]'));
    if (empty) empty.hidden = matches > 0;
  }

  #initRanges() {
    for (const range of this.querySelectorAll('[data-pg-range]')) this.#inputsToThumbs(range);
  }

  /** @param {Element} range */
  #parts(range) {
    const max = Number(/** @type {HTMLElement} */ (range).dataset.max) || 0;
    const [minThumb, maxThumb] = /** @type {NodeListOf<HTMLInputElement>} */ (range.querySelectorAll('[data-pg-range-thumb]'));
    const [minInput, maxInput] = /** @type {NodeListOf<HTMLInputElement>} */ (range.querySelectorAll('[data-pg-range-input]'));
    return { max, minThumb, maxThumb, minInput, maxInput };
  }

  /** @param {Element} range */
  #paint(range, from, to) {
    const { max } = this.#parts(range);
    const el = /** @type {HTMLElement} */ (range);
    el.style.setProperty('--pg-from', String(max ? from / max : 0));
    el.style.setProperty('--pg-to', String(max ? to / max : 1));
  }

  /** @param {Element} range @param {HTMLInputElement} thumb */
  #thumbToInputs(range, thumb) {
    const { max, minThumb, maxThumb, minInput, maxInput } = this.#parts(range);
    let from = Number(minThumb.value);
    let to = Number(maxThumb.value);
    if (from > to) {
      if (thumb === minThumb) from = to;
      else to = from;
      minThumb.value = String(from);
      maxThumb.value = String(to);
    }
    minInput.value = from > 0 ? String(from) : '';
    maxInput.value = to < max ? String(to) : '';
    this.#paint(range, from, to);
  }

  /** @param {Element} range */
  #inputsToThumbs(range) {
    const { max, minThumb, maxThumb, minInput, maxInput } = this.#parts(range);
    if (!minThumb || !maxThumb) return;
    const from = Math.min(Math.max(Number(minInput.value) || 0, 0), max);
    const to = Math.max(Math.min(maxInput.value === '' ? max : Number(maxInput.value), max), from);
    minThumb.value = String(from);
    maxThumb.value = String(to);
    this.#paint(range, from, to);
  }

  // ── In stock by default ─────────────────────────────────────────────────

  #applyDefaultStock() {
    if (!this.hasAttribute('data-default-stock')) return;
    // A shopper preference, not content: crawlers keep the server-rendered, unfiltered grid and
    // its clean ?page=N links (the filtered URLs are noindex).
    if (/bot|crawl|spider|slurp|Google-InspectionTool|Lighthouse/i.test(navigator.userAgent)) return;
    const params = new URLSearchParams(location.search);
    if (params.has(STOCK_PARAM) || storage('get') === '1') return;
    const input = /** @type {HTMLInputElement|null} */ (
      this.refs.form?.querySelector(`input[name="${STOCK_PARAM}"][value="1"]:not(:disabled)`)
    );
    if (!input) return;
    input.checked = true;
    this.render(this.#formUrl(), { soft: true });
  }

  /** Remember a shopper who removed the in-stock filter, so it isn't forced back on. */
  #rememberStock(url) {
    if (!this.hasAttribute('data-default-stock')) return;
    const has = new URL(url, location.origin).searchParams.has(STOCK_PARAM);
    storage(has ? 'remove' : 'set', '1');
  }
}

/**
 * <pg-readmore>: clamps a long collection intro to a few lines with a toggle.
 * The toggle only appears when the text actually overflows.
 */
class PgReadmore extends PgElement {
  mount() {
    const { body, toggle } = this.refs;
    if (!body || !toggle) return;
    const overflowing = body.scrollHeight > body.clientHeight + 4;
    toggle.hidden = !overflowing;
    if (!overflowing) this.classList.add('is-open');
    toggle.addEventListener('click', () => {
      const open = !this.classList.contains('is-open');
      this.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = (open ? toggle.dataset.less : toggle.dataset.more) || '';
    }, { signal: this.signal });
  }
}

/**
 * <pg-search-tabs>: the "Articole și pagini" tab only shows when articles or pages match.
 * Predictive search only matches titles, so this asks the search page itself (this section,
 * `type=article,page`) once the browser is idle, and adds the count to the tab.
 */
class PgSearchTabs extends PgElement {
  mount() {
    const tab = this.refs.content;
    if (!tab || !tab.hidden || !this.dataset.contentUrl) return;
    const run = async () => {
      try {
        const html = await fetchSection(this.dataset.sectionId || '', this.dataset.contentUrl, { key: 'pg-search-tabs' });
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const count = doc.querySelectorAll('.pg-search-page__content > li').length;
        if (!count) return;
        const label = tab.querySelector('[data-count]');
        const total = doc.querySelector('.pg-search-page__count')?.getAttribute('data-total');
        if (label && total) label.textContent = `(${total})`;
        tab.hidden = false;
      } catch {
        /* no tab: products remain the whole page */
      }
    };
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 1200);
  }
}

define('pg-facets', PgFacets);
define('pg-readmore', PgReadmore);
define('pg-search-tabs', PgSearchTabs);
